import React, { useEffect, useState, useRef } from 'react';
import { FaStar, FaRegStar } from "react-icons/fa";
import { apiFetch } from "../../../../apiFetch";
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getFlagCode } from '../../../../utils/countryFlags';
import ProfileUnavailableBanner from "../../../ProfileUnavailableBanner";
import './PlayerProfile.css';



const PlayerProfile = () => {
  const { profileSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // 1) Who is viewing? (role)
  const stateViewerRole = location.state?.viewerRole;
  const storedRole = (
    localStorage.getItem('userRole') ||
    localStorage.getItem('role') ||
    ''
  ).toLowerCase();

  const viewerRole = (stateViewerRole || storedRole || 'player').toLowerCase();

  // 2) Who is viewing? (id)
  let viewerId = location.state?.viewerId || null;
  if (!viewerId && viewerRole) {
    const idKeyMap = {
      player: 'playerId',
      agent: 'agentId',
      scout: 'scoutId',
      club: 'clubId',
    };
    const key = idKeyMap[viewerRole] || 'userId';
    viewerId = localStorage.getItem(key);
  }


  const [playerData, setPlayerData] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [displayImage, setDisplayImage] = useState(null);
  const [averageRating, setAverageRating] = useState(null);
  const [ratingCount, setRatingCount] = useState(null);

  const [showTransfermarktOptions, setShowTransfermarktOptions] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [showTransfermarktModal, setShowTransfermarktModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [disclaimerMessage, setDisclaimerMessage] = useState('');
  const [linkSaved, setLinkSaved] = useState(false);
  const [canEditTransfermarkt, setCanEditTransfermarkt] = useState(true);

  const [showUnderageModal, setShowUnderageModal] = useState(false);
  const [showUnderageRequestModal, setShowUnderageRequestModal] = useState(false);
  const [viewerProfileSlug, setViewerProfileSlug] = useState(null);
  const [underageIntent, setUnderageIntent] = useState('');
  const [underageMessage, setUnderageMessage] = useState('');
  const [underageError, setUnderageError] = useState('');
  const [weeklyProfileViews, setWeeklyProfileViews] = useState(null);
  const hasRecordedView = useRef(false);






  // ADD this ↓
  const [viewerStatus, setViewerStatus] = useState({
    role: viewerRole,
    is_verified: false,
  });




  const [isSigned, setIsSigned] = useState(false);
  const [expiryDate, setExpiryDate] = useState(null);
  // ✅ NEW: store full contract status payload (active + temp lock)
  const [contractStatus, setContractStatus] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [showSignedModal, setShowSignedModal] = useState(false);
  // ✅ Temp-lock modal (approved but unpaid within 24h)
  const [showTempLockModal, setShowTempLockModal] = useState(false);
  // ✅ NEW: Offer-to-sign capacity
  const [offerCapacity, setOfferCapacity] = useState(null);
  const [showNoSlotsModal, setShowNoSlotsModal] = useState(false);
  const [tempLockUntil, setTempLockUntil] = useState(null);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [playerId, setPlayerId] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);





  const formatDate = (date) => {
    const newDate = new Date(date);
    const day = newDate.getDate().toString().padStart(2, '0');
    const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
    const year = newDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return `${years} Years ${months} Months`;
  };

  const calculateAgeInYears = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };


  const isPlayerUnderage = (player) => {
    if (!player?.dob) return false;
    return calculateAgeInYears(player.dob) < 18;
  };




  useEffect(() => {
    const fetchPlayerData = async () => {
      if (!profileSlug) {
        setError('Profile slug not found in the URL');
        return;
      }

      try {
        const qs = new URLSearchParams();
        qs.set("viewerRole", viewerRole);
        if (viewerId) qs.set("viewerId", String(viewerId));

        const response = await apiFetch(`/api/player-profile/${profileSlug}?${qs.toString()}`);


        if (!response.ok) {
          throw new Error(`Player not found. Status Code: ${response.status}`);
        }

        const data = await response.json();

        // 🔍 DEBUG LOGS (THIS IS WHAT WE NEED)
        console.log('📦 RAW backend player data:', data);
        console.log('🏳️ Nationality from backend:', data.nationality);
        console.log('🏳️ Club country from backend:', data.clubCountry);
        console.log(
          '🏳️ Flag code from getFlagCode:',
          getFlagCode(data.nationality || data.clubCountry)
        );

        // ✅ Save internal DB id
        setPlayerId(data.id);

        const normalizedData = {
          ...data,
          profileSlug: data.profileSlug || data.slug,
          representationStatus:
            data.representationStatus ||
            (data.status === 'Represented'
              ? 'Represented (Has Agent)'
              : 'Free Agent (No Agent)'),
        };


        console.log('🧠 NORMALIZED playerData:', normalizedData);

        const displayName =
          normalizedData.fullName && normalizedData.fullName.trim().length > 0
            ? normalizedData.fullName.trim()
            : `${normalizedData.firstName || ''} ${normalizedData.lastName || ''}`.trim() || 'Not Available';


        setPlayerData({
          ...normalizedData,
          dob: normalizedData.dob, // ✅ CORRECT — matches PlayerData table
          displayName: displayName || 'Not Available',
        });

        setEditableData({
          ...normalizedData,
          dob: normalizedData.dob, // ✅ CORRECT
          displayName: displayName || 'Not Available',
        });




        setCanEditTransfermarkt(data.canEditTransfermarkt ?? true);

        if (data.displayImageUrl) setDisplayImage(data.displayImageUrl);
        if (data.transfermarktUrl) setLinkInput(data.transfermarktUrl);

      } catch (err) {
        setError(`Error fetching player data: ${err.message}`);
      }
    };

    fetchPlayerData();
  }, [profileSlug, viewerRole, viewerId]);



  useEffect(() => {
    // only role users need this
    if (viewerRole === "player") return;
    if (!profileSlug) return;

    apiFetch(`/api/video-subscriptions/${profileSlug}/status`)
      .then(res => res.json())
      .then(data => setIsSubscribed(!!data.subscribed))
      .catch(() => { });
  }, [profileSlug, viewerRole]);



  useEffect(() => {
    if (!playerId) return;

    // 👁️ Record view (ONLY non-players, ONCE)
    if (viewerRole !== 'player' && !hasRecordedView.current) {
      hasRecordedView.current = true;

      apiFetch(`/api/player/${playerId}/profile-view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ viewerId, viewerRole }),
      }).catch(() => { });

    }

    // 📊 Fetch weekly views (ONLY player)
    if (viewerRole === 'player') {
      apiFetch(`/api/player/${playerId}/profile-views/weekly`)
        .then(res => res.json())

        .then(data => {
          setWeeklyProfileViews(data.weeklyViews ?? 0);
        })
        .catch(() => { });
    }
  }, [playerId, viewerRole, viewerId]);





  useEffect(() => {
    // Only role users (agent/scout/club) can sign
    if (!viewerRole || viewerRole === "player") return;

    (async () => {
      try {
        const res = await apiFetch("/api/offer-to-sign-capacity");
        if (!res.ok) {
          // don't hard-fail UI; just leave null
          return;
        }
        const data = await res.json();
        setOfferCapacity(data);
      } catch (e) {
        // silent fail; backend is source of truth anyway
      }
    })();
  }, [viewerRole]);





  useEffect(() => {
    if (!playerId) return;

    const fetchRating = async () => {
      try {
        const response = await apiFetch(`/api/player-average-rating/${playerId}`);
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Rating failed: ${response.status} ${text}`);
        }
        const data = await response.json();

        setAverageRating(parseFloat(data.averageRating));
        setRatingCount(data.ratingCount);
      } catch (err) {
        console.error('Error fetching rating:', err);
      }
    };

    const fetchContractStatus = async () => {
      try {
        const res = await apiFetch(`/api/player/${playerId}/contract-status`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Contract status failed: ${res.status} ${text}`);
        }

        const data = await res.json();

        // ✅ NEW: store the full response so we can read isTempLocked, lockExpiresAt
        setContractStatus(data);

        // ✅ Existing active-contract behavior (unchanged)
        if (data.signed && data.expiryDate && new Date(data.expiryDate) > new Date()) {
          setIsSigned(true);
          setExpiryDate(new Date(data.expiryDate));
        } else {
          // ✅ keeps UI correct if contract expires / becomes inactive
          setIsSigned(false);
          setExpiryDate(null);
        }
      } catch (err) {
        console.error('Error fetching contract status:', err);
      }
    };

    fetchRating();
    fetchContractStatus();
  }, [playerId]);



  useEffect(() => {
    if (!expiryDate) return;

    const interval = setInterval(() => {
      const now = new Date();
      const distance = expiryDate - now;

      if (distance <= 0) {
        setCountdown("Now Available");
        setIsSigned(false); // Mark player as unsigned
        clearInterval(interval);
      } else {
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((distance / 1000 / 60) % 60);
        const seconds = Math.floor((distance / 1000) % 60);
        setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryDate]);




  // ✅ Load viewer verification (ROLE-AWARE: agent / scout / club)
  useEffect(() => {
    // Players do not require verification
    if (viewerRole === 'player') return;

    const roleIdMap = {
      agent: 'agentId',
      scout: 'scoutId',
      club: 'clubId',
    };

    const id = viewerId || localStorage.getItem(roleIdMap[viewerRole]);


    if (!id || !viewerRole) return;

    (async () => {
      try {
        // ✅ ROLE-AWARE endpoint
        const res = await apiFetch(`/api/${viewerRole}/${id}/profile`);


        if (!res.ok) return;

        const data = await res.json();

        const isVerified =
          data.verificationStatus === 'approved' || data.isVerified === true;

        setViewerStatus({
          role: viewerRole,
          is_verified: isVerified,
        });

        // 🔥 ADD THIS
        setViewerProfileSlug(data.slug || data.profileSlug || null);

      } catch (err) {
        console.error('Error loading viewer verification:', err);
        // keep default is_verified = false on error
      }
    })();
  }, [viewerRole, viewerId]);





  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('userId', playerId);


    try {
      const response = await apiFetch("/api/upload-display-image", {
        method: "POST",
        body: formData, // ✅ do NOT set Content-Type for FormData
      });


      const data = await response.json();

      if (data.imageUrl) {
        setDisplayImage(data.imageUrl);
        setEditableData(prev => ({ ...prev, displayImageUrl: data.imageUrl }));
      } else {
        alert('Upload failed');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };



  const handleSave = async () => {
    try {
      const payload = {
        ...editableData,
        status:
          editableData.representationStatus === 'Represented (Has Agent)'
            ? 'Represented'
            : 'Free Agent',
      };

      // 🔒 REMOVE LOCKED / VERIFIED FIELDS
      delete payload.primaryPosition;
      delete payload.secondaryPosition;
      delete payload.preferredFoot;
      delete payload.playerStrength;

      // UI-only field
      delete payload.representationStatus;

      const response = await apiFetch(`/api/player-details/${playerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });


      if (!response.ok) {
        throw new Error('Error updating profile');
      }

      const updated = await response.json();

      setPlayerData(prev => ({
        ...prev,
        ...updated,
        representationStatus:
          updated.status === 'Represented'
            ? 'Represented (Has Agent)'
            : 'Free Agent (No Agent)',
      }));

      setEditableData(prev => ({
        ...prev,
        ...updated,
      }));

      setError(null);
      setIsEditing(false);
    } catch (err) {
      setError(`Error saving player data: ${err.message}`);
    }
  };



  const handleCancel = () => {
    setEditableData(playerData);
    setIsEditing(false);
  };



  const handleMessagePlayer = async () => {
    const isMinor = isPlayerUnderage(playerData);



    const rolesRequiringVerification = new Set(['agent', 'scout', 'club']);
    const role = (viewerStatus.role || viewerRole || 'agent').toLowerCase();

    const roleIdMap = {
      agent: 'agentId',
      scout: 'scoutId',
      club: 'clubId',
      player: 'playerId',
    };

    const senderId = localStorage.getItem(roleIdMap[role]);


    if (!senderId) {
      alert('Please log in.');
      return;
    }

    /* ======================================================
       👶 UNDERAGE ACCESS CHECK (UX ONLY)
       Backend is the single source of truth
    ====================================================== */
    // 👶 UNDERAGE — FRONTEND UX ONLY
    if (
      isMinor &&
      rolesRequiringVerification.has(role) &&
      !viewerStatus.is_verified
    ) {
      setShowUnderageModal(true);
      return;
    }


    /* ======================================================
       💬 START / OPEN CONVERSATION
    ====================================================== */
    try {
      const res = await apiFetch("/api/conversations/start", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participant1_id: parseInt(senderId, 10),
          participant2_id: parseInt(playerId, 10),
          initiated_by: parseInt(senderId, 10),
          initiatorRole: viewerRole,
        }),
      });

      // 🔒 Backend hard-block (authoritative)
      if (res.status === 403) {
        if (!viewerStatus.is_verified) {
          setShowUnderageModal(true);
        } else {
          setShowUnderageRequestModal(true);
        }
        return;
      }


      const conversation = await res.json();

      if (!conversation?.id) {
        alert('Failed to start conversation');
        return;
      }

      const roleDashboardPathMap = {
        agent: '/agent-dashboard/chats',
        scout: '/scout-dashboard/chats',
        club: '/club-dashboard/chats',
      };

      const dashboardPath =
        roleDashboardPathMap[viewerRole] || '/agent-dashboard/chats';

      navigate(dashboardPath, {
        state: {
          userId: parseInt(senderId, 10),
          selectedPlayerId: parseInt(playerId, 10),
          selectedPlayerName: playerData?.displayName || playerData?.fullName || "",
          selectedPlayerSlug: playerData?.profileSlug || profileSlug || "",
          openConversationId: conversation.id,
          openFullChat: true,
        },
      });

    } catch (err) {
      console.error('❌ Error starting conversation:', err);
      alert('Something went wrong while starting the conversation.');
    }
  };












  const handleShowInterest = async () => {
    // Map roles to their localStorage keys
    const roleIdMap = {
      agent: 'agentId',
      scout: 'scoutId',
      club: 'clubId',
    };

    const senderRole = (viewerRole || '').toLowerCase();
    const senderId = localStorage.getItem(roleIdMap[senderRole]);
    const playerIdToSend = playerId;


    if (!senderId) {
      alert(`${senderRole || 'User'} not logged in`);
      console.error(`❌ ${senderRole} ID not found in localStorage`);
      return;
    }

    console.log('📤 Attempting to show interest with data:', {
      senderId,
      senderRole,
      playerId: playerIdToSend,
    });



    try {
      const res = await apiFetch("/api/show-interest", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderId,
          senderRole,
          playerId: playerIdToSend,
        }),
      });

      const result = await res.json();
      console.log('📥 Server response:', result);

      if (res.ok) {
        alert('✅ Your interest has been sent to the player.');
        setShowSignedModal(false); // Close modal after success
      } else {
        alert(result.error || '⚠️ Failed to send interest.');
      }
    } catch (err) {
      console.error('❌ Network or server error while sending interest:', err);
      alert('Could not send interest. Please try again later.');
    }
  };






  const handleSendUnderageRequest = async ({
    intent = '',
    message = '',
  } = {}) => {
    // 🔑 Map roles to their stored IDs
    const roleIdMap = {
      agent: 'agentId',
      scout: 'scoutId',
      club: 'clubId',
    };

    const senderRole = (viewerRole || '').toLowerCase();
    const senderId = localStorage.getItem(roleIdMap[senderRole]);

    // 🛑 Hard safety guards
    if (!senderId) {
      alert('Please log in.');
      return;
    }

    if (!viewerStatus?.is_verified) {
      alert('You must be verified to contact underage players.');
      return;
    }

    if (!playerData || !playerId) {
      console.warn('❌ Missing player data for underage request');
      return;
    }

    try {
      const response = await apiFetch("/api/underage-message-request",
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // 🔐 Sender
            senderRole,                           // agent | scout | club
            senderId: parseInt(senderId, 10),

            // 👶 Player
            playerId,                             // internal DB id
            playerFullName: playerData.fullName,
            playerDOB: playerData.dob,

            // 🧠 Intent (structured, safe)
            intent,                               // e.g. "Trial / Evaluation"
            message,                              // short professional message (optional)

            // 🛡️ Metadata (future-proofing)
            requestedAt: new Date().toISOString(),
            source: 'player-profile',
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || '❌ Failed to send underage contact request.');
        return;
      }

      alert('✅ Contact request sent. The player will be notified.');

      // Close modal(s)
      setShowUnderageModal(false);
      if (typeof setShowUnderageRequestModal === 'function') {
        setShowUnderageRequestModal(false);
      }

    } catch (err) {
      console.error('❌ Error sending underage contact request:', err);
      alert('❌ Something went wrong. Please try again later.');
    }
  };






  const handleWatchPlayerVideos = () => {
    const slug = playerData?.profileSlug;
    const pid = playerId || playerData?.id;

    if (!slug) {
      console.error("❌ profileSlug missing for player");
      return;
    }

    navigate(`../video-profile/${slug}`, {
      state: {
        viewerRole,
        viewerId,
        playerId: pid,           // player's numeric ID
        playerProfileSlug: slug, // ✅ pass slug explicitly
        from: "player-profile",
      },
    });
  };





  const handleToggleVideoSubscription = async () => {
    if (!profileSlug) return;
    if (!viewerRole || viewerRole === "player") return;

    try {
      const res = await apiFetch(`/api/video-subscriptions/${profileSlug}`, {
        method: isSubscribed ? "DELETE" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscriberRole: viewerRole }),
      });

      if (!res.ok) {
        alert("Video subscription update failed");
        return;
      }

      setIsSubscribed(prev => !prev);
    } catch (err) {
      console.error("Video subscription error:", err);
      alert("Video subscription update failed");
    }
  };






  const renderStars = (rating) => {
    let computedRating = null;
    if (rating >= 5) computedRating = 5;
    else if (rating >= 4) computedRating = 4;
    else if (rating >= 3) computedRating = 3;
    else if (rating >= 2) computedRating = 2;
    else if (rating >= 1) computedRating = 1;

    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} style={{ color: i <= computedRating ? '#FFD700' : '#ccc' }}>★</span>
      );
    }
    return stars;
  };

  const handleSaveTransfermarktLink = async () => {
    if (!linkInput.startsWith('http')) {
      alert('Please enter a valid link.');
      return;
    }

    try {
      const response = await apiFetch(`/api/player/transfermarkt/${playerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transfermarktUrl: linkInput }),
      });


      if (response.ok) {
        setPlayerData(prev => ({ ...prev, transfermarktUrl: linkInput }));
        setShowTransfermarktOptions(false);
      } else {
        alert('Failed to save link.');
      }
    } catch (err) {
      console.error('Error saving Transfermarkt link:', err);
    }
  };


  if (error) {
    return (
      <ProfileUnavailableBanner
        title="Profile Unavailable"
        message="This profile is not available right now."
        hint="You'll be able to View Profile once player is back."
        showBackButton={true}
      />
    );
  }


  if (!playerData) return <div>Loading...</div>;

  return (
    <div className={`player-profile ${viewerRole}`}>
      <div className="player-profile-container">
        <div className="profile-header">
          <div className="header-left">
            {viewerRole === 'player' && weeklyProfileViews !== null && (
              <span className="profile-views">
                👁 {weeklyProfileViews || '—'} views · last 7 days
              </span>
            )}



          </div>

          <h1 className="header-title">Player Profile</h1>

          <div className="header-right" />
        </div>

        <div className="profile-content">
          <div className="player-profile-content">
            <div className="player-name-row">
              <div className="player-name-left">
                <span className="player-name">
                  Full Name: {playerData.displayName}
                </span>

                {(() => {
                  const country =
                    playerData?.nationality ||
                    playerData?.clubCountry ||
                    null;

                  const flagCode = country ? getFlagCode(country) : null;
                  if (!flagCode) return null;

                  return (
                    <img
                      src={`/flags/${flagCode}.svg`}
                      alt={country}
                      title={country}
                      className="nationality-flag"
                    />
                  );
                })()}
              </div>

              {/* ⭐ Subscribe Star on far right */}
              {viewerRole !== "player" && (
                <span
                  className="subscribe-star"
                  onClick={handleToggleVideoSubscription}
                  title={
                    isSubscribed
                      ? "Subscribed — click to unsubscribe"
                      : "Subscribe to player videos"
                  }
                >
                  {isSubscribed
                    ? <FaStar size={18} color="#ffd400" />
                    : <FaRegStar size={18} color="#777" />
                  }
                </span>
              )}
            </div>

            <p>
              Date of Birth:{' '}
              {playerData.dob ? formatDate(playerData.dob) : 'Not Available'}
              {!isEditing && playerData.dob && <> ({calculateAge(playerData.dob)})</>}
            </p>

            <p>
              {isEditing ? (
                <div className="split-input-container">
                  <input
                    type="text"
                    name="currentClub"
                    value={editableData.currentClub || ''}
                    onChange={handleInputChange}
                    className="half-text-bar"
                    placeholder="Current Club"
                  />
                  <input
                    type="text"
                    name="clubCountry"
                    value={editableData.clubCountry || ''}
                    onChange={handleInputChange}
                    className="half-text-bar"
                    placeholder="Club Country"
                  />
                </div>
              ) : (
                `Current Club: ${playerData.currentClub || 'Not Available'}, Club Country: ${playerData.clubCountry || 'Not Available'}`
              )}
            </p>

            <p>
              {isEditing ? (
                <input
                  type="text"
                  name="previousClub"
                  value={editableData.previousClub || ''}
                  onChange={handleInputChange}
                  className="text-bar"
                  placeholder="e.g. Liverpool – 2025"
                />
              ) : (
                `Previous Club: ${playerData.previousClub || 'Not Available'}`
              )}
            </p>

            <p>
              {isEditing ? (
                <select
                  name="representationStatus"
                  value={editableData.representationStatus || ''}
                  onChange={handleInputChange}
                  className="text-bar"
                >
                  <option value="">Select Representation Status</option>
                  <option value="Free Agent (No Agent)">Free Agent (No Agent)</option>
                  <option value="Represented (Has Agent)">Represented (Has Agent)</option>
                </select>
              ) : (
                <>
                  <strong>Representation Status:</strong>{' '}
                  {playerData.representationStatus || 'Not Available'}
                </>
              )}
            </p>

            {/* 🔒 LOCKED POSITIONS */}
            {!isEditing && (
              <p>
                <strong>Primary Position:</strong>{' '}
                {playerData.primaryPosition || 'Not Available'},{' '}
                <strong>Secondary Position:</strong>{' '}
                {playerData.secondaryPosition || 'Not Available'}
              </p>
            )}


            {/* 🔒 LOCKED HEIGHT & WEIGHT (VISIBLE IN ALL MODES) */}
            <p>
              <strong>Height:</strong>{' '}
              {playerData.height ? playerData.height + ' m' : 'Not Available'},{' '}
              <strong>Weight:</strong>{' '}
              {playerData.weight ? playerData.weight + ' kg' : 'Not Available'}
            </p>



            {/* 🔒 LOCKED FOOT & STRENGTH */}
            <p>
              <strong>Preferred Foot:</strong>{' '}
              {playerData.preferredFoot || 'N/A'},{' '}
              <strong>Strength:</strong>{' '}
              {playerData.playerStrength || 'N/A'}
            </p>


            {viewerRole === 'player' ? (
              !isEditing ? (
                <button
                  className="edit-button"
                  onClick={() => {
                    setError(null);
                    setIsEditing(true);
                  }}
                >
                  Edit
                </button>

              ) : (
                <>
                  {error && (
                    <div className="form-error">
                      {error}
                    </div>
                  )}

                  <div className="edit-actions">
                    <button className="save-button" onClick={handleSave}>
                      Save
                    </button>
                    <button className="cancel-button" onClick={handleCancel}>
                      Cancel
                    </button>
                  </div>
                </>
              )


            ) : (
              <>
                <button
                  className="message-button"
                  onClick={handleWatchPlayerVideos}
                >
                  Watch Player Videos
                </button>

                <button
                  className="message-button"
                    onClick={() => setShowBetaModal(true)}
                >
                  Message Player
                </button>

                <button
                  className="message-button"
                  onClick={async () => {
                    const isMinor = isPlayerUnderage(playerData);

                    const rolesRequiringVerification = new Set(['agent', 'scout', 'club']);
                    const role = (viewerStatus.role || viewerRole || 'agent').toLowerCase();
                    const needsGate =
                      isMinor &&
                      rolesRequiringVerification.has(role) &&
                      !viewerStatus.is_verified;

                    if (needsGate) {
                      setShowUnderageModal(true);
                      return;
                    }

                    // ✅ 24h temp lock (approved but unpaid within activation window)
                    if (contractStatus?.isTempLocked) {
                      const normalizedViewerId =
                        viewerId !== null && viewerId !== undefined
                          ? parseInt(viewerId, 10)
                          : null;

                      const lockedByIdNum =
                        contractStatus.lockedById !== null &&
                          contractStatus.lockedById !== undefined
                          ? parseInt(contractStatus.lockedById, 10)
                          : null;

                      const lockOwnedByViewer =
                        normalizedViewerId !== null &&
                        lockedByIdNum !== null &&
                        contractStatus.lockedByRole?.toLowerCase() === viewerRole.toLowerCase() &&
                        lockedByIdNum === normalizedViewerId;

                      // ❗ If someone else owns the lock → show modal (block)
                      if (!lockOwnedByViewer) {
                        const until = contractStatus.lockExpiresAt
                          ? new Date(contractStatus.lockExpiresAt)
                          : null;

                        setTempLockUntil(until);
                        setShowTempLockModal(true);
                        return;
                      }

                      // 🚧 Beta mode: block resume offer
                      setShowBetaModal(true);
                      return;
                    }

                    // ✅ NEW: signing capacity gate (no slots left → show modal)
                    try {
                      // Prefer fresh capacity check (authoritative), fallback to cached state
                      let cap = offerCapacity;

                      const capRes = await apiFetch("/api/offer-to-sign-capacity");
                      if (capRes.ok) {
                        cap = await capRes.json();
                        setOfferCapacity(cap);
                      }

                      if (cap && Number(cap.remainingSlots) <= 0) {
                        setShowNoSlotsModal(true);
                        return;
                      }
                    } catch (e) {
                      // If capacity check fails, don't block here.
                      // Backend should still enforce capacity in the offer creation endpoint.
                    }

                    if (isSigned) {
                      setShowSignedModal(true);
                    } else {
                      setShowBetaModal(true);
                      return;
                    }
                  }}
                >
                  Offer to Sign Player
                </button>
              </>
            )}


            {viewerRole === 'player' && canEditTransfermarkt && (
              <button
                className="transfermarkt-button"
                onClick={() => {
                  setShowTransfermarktModal(true);
                  setLinkSaved(!!playerData.transfermarktUrl);
                }}
              >
                {playerData.transfermarktUrl ? 'Edit VSN Index Profile' : 'Add VSN Index Profile Link'}
              </button>
            )}

            {showTransfermarktModal && canEditTransfermarkt && (
              <div className="transfermarkt-modal-overlay">
                <div className="transfermarkt-modal">
                  <h2>Add VSN Index Profile Link</h2>

                  <div className="inline-toast">
                    VSN Index is launching soon. Apply now and your profile will be among the first published.
                  </div>



                  <select
                    value={selectedOption}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedOption(value);
                      if (value === 'apply') {
                        setDisclaimerMessage(
                          'Only players currently registered with an officially recognized local football club can apply.'
                        );
                        setTimeout(() => {
                          setDisclaimerMessage('');
                        }, 5000);
                      } else {
                        setDisclaimerMessage('');
                      }
                    }}
                    className="modal-select"
                  >
                    <option value="">Select an option</option>
                    <option value="link">Add VSN Index Profile Link</option>
                    <option value="apply">Apply for VSN Index via VSN</option>
                  </select>

                  {selectedOption === 'apply' && disclaimerMessage && (
                    <div className="inline-toast">
                      {disclaimerMessage}
                    </div>
                  )}

                  {selectedOption === 'link' && (
                    <div style={{ marginTop: '10px' }}>
                      <input
                        type="text"
                        placeholder="Paste your VSN Index Profile Link"
                        value={linkInput}
                        onChange={(e) => setLinkInput(e.target.value)}
                        className="modal-input"
                        disabled={linkSaved}
                      />
                      <button
                        className="modal-button"
                        onClick={handleSaveTransfermarktLink}
                        disabled={linkSaved}
                      >
                        {linkSaved ? 'Saved' : 'Save'}
                      </button>
                    </div>
                  )}

                  {selectedOption === 'apply' && (
                    <div style={{ marginTop: '20px' }}>
                      <button
                        className="modal-button"
                        onClick={() => {
                          setShowTransfermarktModal(false);
                          navigate(`/apply-transfermarkt/${playerId}`);
                        }}
                      >
                        Fill VSN Index Application Form
                      </button>
                    </div>
                  )}

                  <button
                    className="close-button"
                    onClick={() => setShowTransfermarktModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}





          </div>
          <div className="photo-container">
            <div className="photo-placeholder">
              <img
                src={displayImage || "/images/display-photo.png"}
                alt="Display"
                className="player-photo"
              />
              {viewerRole === 'player' && (
                <>
                  <label
                    htmlFor="display-image-input"
                    className="photo-plus-btn"
                    title="Upload a clear head-and-shoulders photo in your team jersey (no action shots)."
                  >
                    +
                  </label>
                  <input
                    id="display-image-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />

                </>
              )}
            </div>

            {averageRating !== null && (
              <div className="vsn-rating-display">
                <strong>VSN Rating:</strong> {renderStars(averageRating)}
                <span style={{ marginLeft: '6px', fontSize: '12px' }}>({ratingCount} ratings)</span>
              </div>
            )}

            {playerData.transfermarktUrl && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <span style={{ fontSize: '1.6rem', color: 'white', fontWeight: 'bold' }}>[</span>
                <a
                  href={playerData.transfermarktUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '1.6rem',
                    color: 'white',
                    textDecoration: 'underline',
                    fontWeight: 'bold'
                  }}
                >
                  VSN Index Profile
                </a>
                <span style={{ fontSize: '1.6rem', color: 'white', fontWeight: 'bold' }}>]</span>
              </div>
            )}




          </div>
        </div>
      </div>

      {showUnderageModal && (
        <div className="player-profile-underage-modal-overlay">
          <div className="player-profile-underage-modal">
            <h2>Verification Required</h2>
            <p>
              This player is underage, only verified Agents, Scouts, and Clubs can
              message or sign underage players. To continue, please request
              verification in your profile.
            </p>
            <div className="modal-buttons">
              <button
                className="modal-button send-button"
                onClick={() => {
                  setShowUnderageModal(false);

                  if (!viewerProfileSlug) {
                    navigate('/login');
                    return;
                  }

                  const role = (viewerStatus.role || 'agent').toLowerCase();

                  const roleProfilePathMap = {
                    agent: `/agent-profile/${viewerProfileSlug}`,
                    scout: `/scout-profile/${viewerProfileSlug}`,
                    club: `/club-profile/${viewerProfileSlug}`,
                  };

                  navigate(roleProfilePathMap[role] || '/login');


                }}

              >
                Go to Profile
              </button>
              <button
                className="modal-button close-button"
                onClick={() => setShowUnderageModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {showUnderageRequestModal && (
        <div className="player-profile-underage-modal-overlay">
          <div className="player-profile-underage-modal">
            <h2>Request Contact with Underage Player</h2>

            <p>
              Please state your intent before contacting this underage player.
              This request will be reviewed by an admin and visible to the
              player’s guardian.
            </p>

            {/* INTENT */}
            <label className="modal-label">
              Intent <span className="required">*</span>
            </label>
            <select
              className="modal-input"
              value={underageIntent}
              onChange={(e) => setUnderageIntent(e.target.value)}
            >
              <option value="">Select intent</option>
              <option value="Trial / Evaluation">Trial / Evaluation</option>
              <option value="Academy Invitation">Academy Invitation</option>
              <option value="Contract Discussion">Contract Discussion</option>
              <option value="Scouting Inquiry">Scouting Inquiry</option>
              <option value="Representation Inquiry">Representation Inquiry</option>
              <option value="Other">Other</option>
            </select>

            {/* MESSAGE */}
            <label className="modal-label">
              Message <span className="required">*</span>
            </label>
            <textarea
              className="modal-textarea"
              value={underageMessage}
              onChange={(e) => setUnderageMessage(e.target.value)}
              placeholder="Briefly explain why you want to contact this player (min 10 characters)"
              rows={4}
              maxLength={300}
            />

            {underageError && (
              <p style={{ color: 'red', marginTop: '6px' }}>
                {underageError}
              </p>
            )}

            {/* ACTIONS */}
            <div className="underage-modal-actions">
              <button
                className="modal-button send-button"
                onClick={() => {
                  if (!underageIntent) {
                    setUnderageError('Please select an intent.');
                    return;
                  }

                  if (!underageMessage || underageMessage.trim().length < 10) {
                    setUnderageError('Message must be at least 10 characters.');
                    return;
                  }

                  setUnderageError('');

                  handleSendUnderageRequest({
                    intent: underageIntent,
                    message: underageMessage.trim(),
                  });

                  setUnderageIntent('');
                  setUnderageMessage('');
                }}
              >
                Send Request
              </button>

              <button
                className="modal-button close-button"
                onClick={() => {
                  setShowUnderageRequestModal(false);
                  setUnderageIntent('');
                  setUnderageMessage('');
                  setUnderageError('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {showTempLockModal && (
        <div className="player-profile-underage-modal-overlay">
          <div
            className="player-profile-underage-modal"
            style={{
              backgroundColor: "black",
              border: "2px solid yellow",
              borderRadius: "8px",
              textAlign: "center",
              padding: "20px",
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            <h2 style={{ color: "yellow", fontSize: "20px" }}>
              ⏳ Approved Offer Pending Payment
            </h2>

            <p style={{ color: "white", fontSize: "16px", marginTop: "10px" }}>
              This player has an approved offer awaiting activation/payment.
            </p>

            <p style={{ color: "white", fontSize: "16px", marginTop: "10px" }}>
              Try again after:{" "}
              <strong>
                {tempLockUntil ? tempLockUntil.toLocaleString() : "later"}
              </strong>
            </p>

            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <button
                onClick={() => setShowTempLockModal(false)}
                style={{
                  padding: "10px 40px",
                  backgroundColor: "yellow",
                  color: "black",
                  fontWeight: "bold",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}




      {showNoSlotsModal && (
        <div className="player-profile-underage-modal-overlay">
          <div
            className="player-profile-underage-modal"
            style={{
              backgroundColor: "black",
              border: "2px solid yellow",
              borderRadius: "8px",
              textAlign: "center",
              padding: "20px",
              maxWidth: "400px",
              margin: "0 auto",
            }}
          >
            <h2 style={{ color: "yellow", fontSize: "20px" }}>
              🚫 Signing Limit Reached
            </h2>

            <p style={{ color: "white", fontSize: "16px", marginTop: "10px" }}>
              You can’t sign more players right now because you’ve exhausted your limit.
            </p>

            <p style={{ color: "white", fontSize: "16px", marginTop: "10px" }}>
              <strong>Active contracts:</strong>{" "}
              {offerCapacity?.activeContracts ?? "—"} / {offerCapacity?.maxCapacity ?? "—"}
            </p>

            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <button
                onClick={() => setShowNoSlotsModal(false)}
                style={{
                  padding: "10px 40px",
                  backgroundColor: "yellow",
                  color: "black",
                  fontWeight: "bold",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}





      {showSignedModal && (
        <div className="player-profile-underage-modal-overlay">
          <div
            className="player-profile-underage-modal"
            style={{
              backgroundColor: 'black',
              border: '2px solid yellow',
              borderRadius: '8px',
              textAlign: 'center',
              padding: '20px',
              maxWidth: '400px',
              margin: '0 auto'
            }}
          >
            <h2 style={{ color: 'yellow', fontSize: '20px' }}>
              ⚠️ The Player Has Already Been Signed
            </h2>

            <p style={{ color: 'white', fontSize: '16px', marginTop: '10px' }}>
              Available in: <strong>{countdown}</strong>
            </p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                marginTop: '20px'
              }}
            >
              <button
                onClick={handleShowInterest}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'yellow',
                  color: 'black',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Show Interest
              </button>

              <button
                onClick={() => setShowSignedModal(false)}
                style={{
                  padding: '10px 40px',
                  backgroundColor: 'yellow',
                  color: 'black',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {showBetaModal && (
        <div className="player-profile-underage-modal-overlay">
          <div
            className="player-profile-underage-modal"
            style={{
              backgroundColor: 'black',
              border: '2px solid yellow',
              borderRadius: '8px',
              textAlign: 'center',
              padding: '20px',
              maxWidth: '420px',
              margin: '0 auto',
            }}
          >
            <h2 style={{ color: 'yellow', fontSize: '20px' }}>
              🚧 Feature Not Available
            </h2>

            <p style={{ color: 'white', fontSize: '15px', marginTop: '12px' }}>
              This feature is currently unavailable.
              <br /><br />
              The app is in <strong>Beta Testing Mode</strong>.
            </p>

            <button
              onClick={() => setShowBetaModal(false)}
              style={{
                marginTop: '20px',
                padding: '10px 40px',
                backgroundColor: 'yellow',
                color: 'black',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}




    </div>
  );
};

export default PlayerProfile;